import type { ProjectSummary } from "@calcplan/domain";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft,
  Download,
  FileBox,
  FileText,
  History,
  Image,
  MessageSquareText,
  Paperclip,
  Save,
  Trash2,
} from "lucide-react";
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, apiAssetUrl, ApiError } from "../lib/api";

type Client = { _id: string; name: string };
type Annotation = { _id: string; title: string; body: string; status: "open" | "resolved"; color: string; updatedAt: string };
type Asset = { _id: string; name: string; mimeType: string; sizeBytes: number; purpose: string; createdAt: string };
type RenderJob = { id: string; state: string; progress: number; result?: { assetId?: string }; createdAt: string };
type HistoryEntry = { id: string; revision: number; commandType: string; actorId: string; createdAt: string };

export function ProjectDetailsPage() {
  const { projectId = "" } = useParams();
  const queryClient = useQueryClient();
  const projectQuery = useQuery({ queryKey: ["project", projectId], queryFn: () => api<{ data: ProjectSummary }>(`/projects/${projectId}`) });
  const clientsQuery = useQuery({ queryKey: ["clients"], queryFn: () => api<{ data: Client[] }>("/clients") });
  const annotationsQuery = useQuery({ queryKey: ["annotations", projectId], queryFn: () => api<{ data: Annotation[] }>(`/projects/${projectId}/annotations`) });
  const assetsQuery = useQuery({ queryKey: ["assets", projectId], queryFn: () => api<{ data: Asset[] }>(`/projects/${projectId}/assets`) });
  const rendersQuery = useQuery({ queryKey: ["renders", projectId], queryFn: () => api<{ data: RenderJob[] }>(`/projects/${projectId}/renders`) });
  const historyQuery = useQuery({ queryKey: ["history", projectId], queryFn: () => api<{ data: HistoryEntry[] }>(`/projects/${projectId}/scene/history`) });
  const [information, setInformation] = useState<Record<string, unknown>>({});
  const [annotation, setAnnotation] = useState({ title: "", body: "" });
  const [feedback, setFeedback] = useState("");

  useEffect(() => {
    if (projectQuery.data) setInformation(projectQuery.data.data.information ?? {});
  }, [projectQuery.data]);

  const saveInfo = useMutation({
    mutationFn: () => api(`/projects/${projectId}`, {
      method: "PATCH",
      body: { expectedRevision: projectQuery.data?.data.currentRevision, information },
    }),
    onSuccess: async () => {
      setFeedback("Informações do projeto salvas.");
      await queryClient.invalidateQueries({ queryKey: ["project", projectId] });
    },
  });
  const createAnnotation = useMutation({
    mutationFn: () => api(`/projects/${projectId}/annotations`, {
      method: "POST",
      body: { ...annotation, status: "open", color: "#F59E0B", anchor: { view: "plan", positionMm: [0, 0, 0], targetId: null } },
    }),
    onSuccess: async () => {
      setAnnotation({ title: "", body: "" });
      await queryClient.invalidateQueries({ queryKey: ["annotations", projectId] });
    },
  });
  const updateAnnotation = useMutation({
    mutationFn: ({ id, status }: { id: string; status: string }) => api(`/projects/${projectId}/annotations/${id}`, { method: "PATCH", body: { status } }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["annotations", projectId] }),
  });
  const deleteAsset = useMutation({
    mutationFn: (assetId: string) => api(`/assets/${assetId}`, { method: "DELETE" }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["assets", projectId] }),
  });
  const uploadAsset = useMutation({
    mutationFn: async (file: File) => {
      if (file.size > 2_000_000) throw new Error("O arquivo deve ter no máximo 2 MB.");
      return api(`/projects/${projectId}/assets`, {
        method: "POST",
        body: { name: file.name, mimeType: file.type, contentBase64: await fileToBase64(file), purpose: "attachment" },
      });
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["assets", projectId] }),
  });
  const exportProject = useMutation({
    mutationFn: async (format: "calcplan-json" | "svg-plan" | "glb") => {
      const started = await api<{ data: { id: string } }>(`/projects/${projectId}/exports`, {
        method: "POST",
        body: { format, idempotencyKey: `project-${projectId}-${format}-${Date.now()}` },
      });
      for (let attempt = 0; attempt < 30; attempt += 1) {
        await new Promise((resolve) => window.setTimeout(resolve, 250));
        const job = await api<{ data: { state: string; result?: { assetId?: string } } }>(`/jobs/${started.data.id}`);
        if (job.data.state === "completed" && job.data.result?.assetId) return job.data.result.assetId;
        if (job.data.state === "failed") throw new Error("A exportação falhou.");
      }
      throw new Error("A exportação excedeu o tempo esperado.");
    },
    onSuccess: (assetId) => {
      window.open(apiAssetUrl(assetId), "_blank", "noopener,noreferrer");
      void assetsQuery.refetch();
    },
  });

  const project = projectQuery.data?.data;
  const error = [saveInfo.error, createAnnotation.error, uploadAsset.error, exportProject.error].find(Boolean);

  if (!project) {
    return <div className="page"><div className="empty-state"><FileBox size={34} /><h2>{projectQuery.isLoading ? "Carregando projeto…" : "Projeto não encontrado"}</h2></div></div>;
  }

  return (
    <div className="page project-details-page">
      <header className="page-header">
        <div><Link className="page-back" to="/dashboard"><ArrowLeft size={17} /> Projetos</Link><span className="page-kicker">Revisão {project.currentRevision}</span><h1>{project.name}</h1><p>{project.statusName} · {project.areaM2.toLocaleString("pt-BR")} m² · {project.roomCount} ambiente(s)</p></div>
        <Link className="button button--primary" to={`/editor?id=${project.id}`}>Abrir editor</Link>
      </header>
      {(feedback || error) && <div className={`assembly-feedback ${error ? "assembly-feedback--error" : ""}`}>{error ? (error instanceof ApiError ? error.message : error instanceof Error ? error.message : "Falha") : feedback}</div>}

      <div className="project-details-grid">
        <section className="project-detail-card project-detail-card--info">
          <header><FileText size={19} /><div><h2>Informações do projeto</h2><p>Dados comerciais e técnicos vinculados à revisão.</p></div></header>
          <div className="project-info-form">
            <label>Cliente<select value={String(information.clientId ?? "")} onChange={(event) => setInformation({ ...information, clientId: event.target.value || null })}><option value="">Sem cliente vinculado</option>{clientsQuery.data?.data.map((client) => <option key={client._id} value={client._id}>{client.name}</option>)}</select></label>
            <label>Endereço<input value={String(information.address ?? "")} onChange={(event) => setInformation({ ...information, address: event.target.value })} /></label>
            <label>Responsável<input value={String(information.owner ?? "")} onChange={(event) => setInformation({ ...information, owner: event.target.value })} /></label>
            <label>Referência<input value={String(information.reference ?? "")} onChange={(event) => setInformation({ ...information, reference: event.target.value })} /></label>
            <label className="wide">Observações<textarea value={String(information.notes ?? "")} onChange={(event) => setInformation({ ...information, notes: event.target.value })} rows={4} /></label>
          </div>
          <footer><button className="button button--primary" onClick={() => saveInfo.mutate()} disabled={saveInfo.isPending}><Save size={16} /> Salvar informações</button></footer>
        </section>

        <section className="project-detail-card">
          <header><Download size={19} /><div><h2>Exportações</h2><p>Saídas geradas a partir da cena canônica salva.</p></div></header>
          <div className="detail-actions">
            <button className="button button--secondary" onClick={() => exportProject.mutate("calcplan-json")} disabled={exportProject.isPending}>Documento Calcme 3D</button>
            <button className="button button--secondary" onClick={() => exportProject.mutate("svg-plan")} disabled={exportProject.isPending}>Planta SVG</button>
            <button className="button button--secondary" onClick={() => exportProject.mutate("glb")} disabled={exportProject.isPending}>Modelo GLB</button>
          </div>
        </section>

        <section className="project-detail-card">
          <header><MessageSquareText size={19} /><div><h2>Anotações</h2><p>Pendências independentes da geometria.</p></div></header>
          <Link className="button button--secondary" to={`/projects/${projectId}/annotations/new`}><MessageSquareText size={16} /> Nova prancheta</Link>
          <div className="annotation-create"><input value={annotation.title} onChange={(event) => setAnnotation({ ...annotation, title: event.target.value })} placeholder="Título da pendência" /><textarea value={annotation.body} onChange={(event) => setAnnotation({ ...annotation, body: event.target.value })} placeholder="Descrição" rows={2} /><button className="button button--primary" onClick={() => createAnnotation.mutate()} disabled={!annotation.title.trim()}>Adicionar</button></div>
          <div className="annotation-list">
            {annotationsQuery.data?.data.map((entry) => <article key={entry._id} className={entry.status === "resolved" ? "resolved" : ""}><i style={{ background: entry.color }} /><span><strong>{entry.title}</strong><p>{entry.body || "Sem descrição"}</p><small>{new Date(entry.updatedAt).toLocaleString("pt-BR")}</small></span><Link className="text-button" to={`/projects/${projectId}/annotations/${entry._id}`}>Editar</Link><button className="text-button" onClick={() => updateAnnotation.mutate({ id: entry._id, status: entry.status === "open" ? "resolved" : "open" })}>{entry.status === "open" ? "Resolver" : "Reabrir"}</button></article>)}
            {!annotationsQuery.data?.data.length && <p className="muted">Nenhuma anotação.</p>}
          </div>
        </section>

        <section className="project-detail-card">
          <header><Paperclip size={19} /><div><h2>Anexos e ativos</h2><p>Referências, imagens e documentos do projeto.</p></div></header>
          <label className="asset-upload"><Paperclip size={18} /><span>{uploadAsset.isPending ? "Enviando…" : "Adicionar arquivo (até 2 MB)"}</span><input type="file" accept="image/png,image/jpeg,image/webp,application/pdf,model/gltf-binary" onChange={(event) => { const file = event.target.files?.[0]; if (file) uploadAsset.mutate(file); event.currentTarget.value = ""; }} /></label>
          <div className="asset-list">
            {assetsQuery.data?.data.map((asset) => <div key={asset._id}><span>{asset.mimeType.startsWith("image/") ? <Image size={18} /> : <FileBox size={18} />}<a href={apiAssetUrl(asset._id)} target="_blank" rel="noreferrer"><strong>{asset.name}</strong><small>{formatBytes(asset.sizeBytes)} · {asset.purpose}</small></a></span><button onClick={() => deleteAsset.mutate(asset._id)} aria-label={`Excluir ${asset.name}`}><Trash2 size={16} /></button></div>)}
          </div>
        </section>

        <section className="project-detail-card">
          <header><Image size={19} /><div><h2>Galeria de render</h2><p>Processamentos rápidos e com IA.</p></div></header>
          <div className="render-gallery">
            {rendersQuery.data?.data.map((render) => <article key={render.id}><div>{render.result?.assetId ? <a href={apiAssetUrl(render.result.assetId)} target="_blank" rel="noreferrer"><Image size={28} /></a> : <Image size={28} />}</div><strong>{render.state}</strong><small>{render.progress}% · {new Date(render.createdAt).toLocaleString("pt-BR")}</small></article>)}
            {!rendersQuery.data?.data.length && <p className="muted">Nenhum render gerado.</p>}
          </div>
        </section>

        <section className="project-detail-card">
          <header><History size={19} /><div><h2>Histórico da cena</h2><p>Comandos persistidos por revisão.</p></div></header>
          <div className="history-list">
            {historyQuery.data?.data.map((entry) => <div key={entry.id}><strong>R{entry.revision}</strong><span>{entry.commandType}</span><time>{new Date(entry.createdAt).toLocaleString("pt-BR")}</time></div>)}
            {!historyQuery.data?.data.length && <p className="muted">Esta cena ainda não possui operações registradas.</p>}
          </div>
        </section>
      </div>
    </div>
  );
}

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error);
    reader.onload = () => resolve(String(reader.result).split(",")[1] ?? "");
    reader.readAsDataURL(file);
  });
}

function formatBytes(bytes: number): string {
  if (bytes < 1_024) return `${bytes} B`;
  if (bytes < 1_048_576) return `${Math.round(bytes / 1_024)} KB`;
  return `${(bytes / 1_048_576).toFixed(1)} MB`;
}
